import {
  Component,
  OnInit,
  computed,
  DestroyRef,
  signal,
  input,
  inject,
  OnDestroy,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";

import { WidgetSettingsModalComponent } from "@modules/grid/components/widget-settings-modal/widget-settings-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { ICurrentUser, UserPermissions } from "@models/user-management/user";
import { NotificationsModalComponent } from "../notifications-modal/notifications-modal.component";
import { ContactSupportModalComponent } from "../contact-support-modal/contact-support-modal.component";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GridDynamicWidgetComponent } from "@modules/grid/components/grid-dynamic-widget/grid-dynamic-widget.component";
import { distinctUntilChanged, filter, map } from "rxjs";
import { AuthService } from "@app/core/auth";
import { NotificationApiService } from "@dmms/shared/data-access/notification/services/notification-api.service";
import { NotificationsHubService } from "@services/notifications/notifications-hub.service";
import { LocalizationService } from "@app/core/localization";
import { LangEnum } from "@models/interface-management/localization";
import { UserRoleEnum } from "@models/user-management/user-role";

@Component({
  selector: "app-toolbar",
  templateUrl: "./toolbar.component.html",
  styleUrl: "./toolbar.component.scss",
  standalone: false,
})
export class ToolbarComponent implements OnInit, OnDestroy {
  user = input<ICurrentUser>();
  applicationName = input<string>();
  notificationCount = signal<number>(0);

  formattedRoleName = computed(() => {
    const roles = this.user()?.roleNames ?? [];

    const roleName =
      roles.length > 1
        ? roles.find((role) => role !== UserRoleEnum.Requestor)
        : roles[0];

    if (!roleName) {
      return "";
    }

    return roleName.replace(/([A-Z])/g, " $1").trim();
  });

  isAdmin = computed(() => {
    return this.user()?.roleNames?.includes(UserRoleEnum.Admin) ?? false;
  });

  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationApiService = inject(NotificationApiService);
  private readonly notificationHubService = inject(NotificationsHubService);
  private readonly localizationService = inject(LocalizationService);
  private readonly document = inject(DOCUMENT);

  hasGridWidget = signal(false);
  isUserMenuOpen = signal(false);
  isLangMenuOpen = signal(false);
  slug = signal<string>("toolbar");

  currentLang = signal<string>(this.localizationService.getCurrentLang());
  currentLangShortName = computed(() => {
    const langList = LocalizationService.getLangList();
    const found = langList.find((l) => l.lang === this.currentLang());
    return found ? found.shortName : "Рус";
  });

  get languages() {
    return LocalizationService.getLangList();
  }

  onLangMenuOpenChange(isOpen: boolean): void {
    this.isLangMenuOpen.set(isOpen);
  }

  closeLangMenu(): void {
    this.isLangMenuOpen.set(false);
  }

  selectLanguage(lang: LangEnum): void {
    this.localizationService.setLang(lang);
    this.currentLang.set(lang);
    this.closeLangMenu();
  }

  readonly gridPermissions = [UserPermissions.AdministrationGridsEdit];

  ngOnInit() {
    this.findComponentInActivatedRoute();
    this.getUnreadCount();
    this.notificationHubService.initializeConnection();
    this.subscribeUnreadCountHub();
  }

  private subscribeUnreadCountHub(): void {
    this.notificationHubService.counts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => this.notificationCount.set(res?.unreadCount ?? 0));
  }

  private getUnreadCount(): void {
    this.notificationApiService
      .getNotificationsUnreadCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res?.isSuccess) {
          this.notificationCount.set(res?.value ?? 0);
        }
      });
  }

  findComponentInActivatedRoute() {
    this.detectGridDynamicWidget(this.getCurrentRoute(this.activatedRoute));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getCurrentRoute(this.activatedRoute)),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((route) => {
        this.detectGridDynamicWidget(route);
      });
  }

  private detectGridDynamicWidget(route: ActivatedRoute): void {
    const component = route.routeConfig?.component;
    this.hasGridWidget.set(component === GridDynamicWidgetComponent);
  }

  private getCurrentRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  reloadPage(): void {
    const url = this.router.url.split("?");

    if (url[0] == "/my-requests") {
      this.document.location.reload();
    }
  }

  openSettingsModal() {
    this.dialog.open(WidgetSettingsModalComponent, {
      width: "1000px",
      height: "700px",
      maxWidth: "calc(100%-10px)",
      maxHeight: "calc(100%-10px)",
      autoFocus: false,
      disableClose: true,
    });
  }

  openNotificationsModal(): void {
    this.dialog.open(NotificationsModalComponent, {
      width: "900px",
      height: "650px",
      autoFocus: false,
    });
  }

  openContactSupportModal(): void {
    this.closeUserMenu();
    this.dialog.open(ContactSupportModalComponent, {
      width: "600px",
      height: "325px",
      autoFocus: false,
    });
  }

  onUserMenuOpenChange(isOpen: boolean): void {
    this.isUserMenuOpen.set(isOpen);
  }

  closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  async openAdminPanel(): Promise<void> {
    this.closeUserMenu();
    if (this.router.url.startsWith("/admin/main/dictionary-table")) {
      return;
    }
    await this.router.navigateByUrl("/admin");
  }

  async openGuide(): Promise<void> {
    this.closeUserMenu();
    if (this.router.url.startsWith("/my-guides")) {
      return;
    }

    await this.router.navigateByUrl("/my-guides");
  }

  async logout(): Promise<void> {
    this.closeUserMenu();
    this.dialog.closeAll();
    await this.authService.logout();
    this.document.location.href = "/auth";
  }

  ngOnDestroy(): void {
    this.notificationHubService.stopConnection();
  }
}
